import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench, Gauge, Disc3, Cog, Battery, Snowflake, Droplets, CircleDot,
  ArrowRight, CheckCircle2, Star, Users, Sparkles, FileText, Handshake,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DiagnoseFlow } from "@/components/DiagnoseFlow";

const HERO = "https://images.unsplash.com/photo-1552656967-7a0991a13906?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

const FEATURES = [
  {
    icon: Sparkles, title: "AI Diagnosis",
    desc: "Describe the symptoms or scan a fault code and get an instant, expert-grade diagnostic report in seconds.",
    img: "https://images.unsplash.com/photo-1727893380169-4dda123e19f7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
  {
    icon: Handshake, title: "Marketplace",
    desc: "Post the job once and let vetted local pros compete with competitive bids — you pick the best offer.",
    img: "https://images.unsplash.com/photo-1586582636676-9ca2d4cedb9a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
  {
    icon: FileText, title: "Invoicing",
    desc: "Turn approved work into professional invoices and estimates, then share a link and get paid — all in one place.",
    img: "https://images.unsplash.com/photo-1707902665498-a202981fb5ac?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
];

const SERVICES = [
  { icon: Disc3, title: "Brakes", desc: "Pads, rotors, calipers & fluid — stop safely." },
  { icon: Cog, title: "Engine Repair", desc: "Diagnostics, tune-ups, timing & rebuilds." },
  { icon: Gauge, title: "Diagnostics", desc: "Check-engine lights and fault-code analysis." },
  { icon: Droplets, title: "Oil & Fluids", desc: "Oil changes, flushes & scheduled maintenance." },
  { icon: CircleDot, title: "Tires & Alignment", desc: "Rotation, balancing and wheel alignment." },
  { icon: Battery, title: "Electrical", desc: "Batteries, alternators, starters & wiring." },
  { icon: Snowflake, title: "A/C & Heating", desc: "Recharge, leak repair and climate control." },
  { icon: Wrench, title: "Suspension", desc: "Shocks, struts, bushings & steering." },
];

const STEPS = [
  { icon: Sparkles, n: "01", title: "Diagnose", desc: "Tell us what's wrong. Skilled mechanics review your symptoms and vehicle health, backed by intelligent diagnostic tools." },
  { icon: Users, n: "02", title: "Compare", desc: "Local pros in the Pitcrew network view your request and send you bids." },
  { icon: Star, n: "03", title: "Choose", desc: "Review mechanic ratings, choose the best offer, and get back on the road." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const openForm = () => setOpen(true);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      {/* Nav */}
      <header className="sticky top-0 z-30 h-16 border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto h-full px-6 flex items-center gap-3">
          <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew — Your Whole Team. One Click."
            data-testid="landing-logo" className="h-9 md:h-11 w-auto cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            <Button variant="ghost" size="sm" data-testid="landing-nav-blog" onClick={() => navigate("/blog")}
              className="px-2.5 sm:px-3 text-slate-300 hover:text-white hover:bg-slate-800">Blog</Button>
            {user ? (
              <Button data-testid="landing-dashboard-button" onClick={() => navigate("/dashboard")}
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Go to Dashboard</Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" data-testid="landing-client-login" onClick={() => navigate("/client/login")}
                  className="px-2.5 sm:px-3 text-slate-300 hover:text-white hover:bg-slate-800">Client Login</Button>
                <Button variant="ghost" size="sm" data-testid="landing-signin-button" onClick={() => navigate("/login")}
                  className="px-2.5 sm:px-3 text-slate-300 hover:text-white hover:bg-slate-800">Shop Sign in</Button>
                <Button data-testid="landing-nav-estimate" onClick={openForm}
                  className="hidden sm:inline-flex bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Get Instant AI Diagnosis</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="High-performance engine bay" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-slate-950/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1">
              <Star className="h-3.5 w-3.5" /> The national auto-repair network
            </span>
            <h1 className="font-head font-black text-4xl md:text-6xl text-slate-50 leading-[1.05] mt-5">
              Stop Guessing.<br /><span className="text-amber-400">Start Repairing.</span>
            </h1>
            <p className="text-lg text-slate-300 mt-5 max-w-xl leading-relaxed">
              Get a free, instant AI diagnostic report on your vehicle's symptoms—then receive competitive bids from local pros to fix it.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" data-testid="hero-get-estimate" onClick={openForm}
                className="h-12 px-7 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-base group">
                Get Instant AI Diagnosis
                <ArrowRight className="h-4 w-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-services"
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                className="h-12 px-7 bg-slate-900/60 border-slate-700 text-slate-200 hover:bg-slate-800 text-base">
                How It Works
              </Button>
            </div>
            <div className="flex flex-wrap gap-6 mt-10 text-sm text-slate-400">
              <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-400" /> Expert-backed diagnosis</span>
              <span className="flex items-center gap-2"><Users className="h-4 w-4 text-blue-400" /> Top-rated local pros</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Competitive bids in minutes</span>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-b border-slate-800/60 bg-slate-900/40">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-400">Simple process</p>
            <h2 className="font-head font-bold text-3xl md:text-4xl text-slate-50 mt-2">How it works</h2>
            <p className="text-slate-400 mt-3">Three steps from symptom to solved — no phone tag, no guesswork.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} data-testid={`step-${s.title.toLowerCase()}`} className="rounded-xl border border-slate-800 bg-slate-900 p-6 transition-transform duration-200 hover:-translate-y-1">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-lg bg-amber-500/15 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-amber-400" />
                    </div>
                    <span className="font-head font-black text-4xl text-slate-800">{s.n}</span>
                  </div>
                  <h3 className="font-head font-bold text-xl text-slate-100 mt-4">{s.title}</h3>
                  <p className="text-slate-400 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform features */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="landing-features">
        <div className="max-w-xl">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-400">The platform</p>
          <h2 className="font-head font-bold text-3xl md:text-4xl text-slate-50 mt-2">Everything you need to win the job</h2>
          <p className="text-slate-400 mt-3">From instant AI diagnosis to getting paid — Pitcrew runs the whole workflow.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 transition-all duration-300 hover:-translate-y-1.5 hover:border-amber-500/40">
                <div className="relative h-44 overflow-hidden">
                  <img src={f.img} alt={f.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/45 to-slate-900/5" />
                  <div className="absolute inset-0 bg-amber-500/15 mix-blend-overlay" />
                  <div className="absolute bottom-3 left-4 h-11 w-11 rounded-lg bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                    <Icon className="h-5 w-5 text-slate-950" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-head font-bold text-xl text-slate-100">{f.title}</h3>
                  <p className="text-slate-400 mt-2 leading-relaxed text-sm">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Social proof / stats */}
      <section className="border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-16" data-testid="landing-stats">
          <div className="max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-400">Join early</p>
            <h2 className="font-head font-bold text-3xl md:text-4xl text-slate-50 mt-2">A Rapidly Growing Network of Local Experts</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {[
              { icon: Users, text: "Connecting you with hand-picked local pros in your neighborhood." },
              { icon: ArrowRight, text: "New mechanics joining daily to provide competitive bids." },
              { icon: Sparkles, text: "Building the future of honest, AI-powered auto repair, one city at a time." },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.text} data-testid={`selling-point-${i}`} className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-amber-400" />
                  </div>
                  <p className="text-slate-300 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-xl">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-400">What we do</p>
          <h2 className="font-head font-bold text-3xl md:text-4xl text-slate-50 mt-2">Full-service auto repair</h2>
          <p className="text-slate-400 mt-3">Whatever your vehicle needs, our shop has it covered.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} data-testid={`service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-xl border border-slate-800 bg-slate-900 p-6 transition-transform duration-200 hover:-translate-y-1">
                <div className="h-11 w-11 rounded-lg bg-amber-500/15 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-amber-400" />
                </div>
                <h3 className="font-head font-bold text-slate-100">{s.title}</h3>
                <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="relative overflow-hidden rounded-2xl border border-amber-500/20 p-10 md:p-14 text-center">
          <img src="https://images.unsplash.com/photo-1653933686284-8414eb9ebdf4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400"
            alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/90 to-slate-900/80" />
          <div className="absolute inset-0 bg-amber-500/5 mix-blend-overlay" />
          <div className="relative">
            <h2 className="font-head font-black text-3xl md:text-4xl text-slate-50">Ready to get back on the road?</h2>
            <p className="text-slate-300 mt-3 max-w-lg mx-auto">Run a free AI diagnosis, then let the Pitcrew network compete for your business — no obligation, just competitive local bids.</p>
            <Button size="lg" data-testid="cta-get-estimate" onClick={openForm}
              className="h-12 px-8 mt-7 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-base group">
              Get Instant AI Diagnosis
              <ArrowRight className="h-4 w-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center gap-3 justify-between text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew" className="h-7 object-contain" data-testid="footer-logo" />
          </div>
          <p>© {new Date().getFullYear()} · Professional auto repair & service</p>
          <div className="flex items-center gap-4">
            <button data-testid="footer-blog" onClick={() => navigate("/blog")} className="hover:text-slate-300">Blog</button>
            <button data-testid="footer-signin" onClick={() => navigate("/login")} className="hover:text-slate-300">Shop owner sign in</button>
          </div>
        </div>
      </footer>

      {/* Diagnosis-first flow */}
      <DiagnoseFlow open={open} onOpenChange={setOpen} />
    </div>
  );
}
